/* eslint-disable unicorn/filename-case */
import { ICourse } from 'interfaces/courses';
import { IExternalItem } from 'interfaces/external_items';
import { IGraduateCourse } from 'interfaces/graduate_courses';
import { IGraduateDiscipline } from 'interfaces/graduate_disciplines';
import { getTenant } from 'services/tenants';
import { useEffect, useMemo, useState } from 'react';
import { useSelect } from '@refinedev/antd';

const selectSearchConfig = {
  optionLabel: 'name' as const,
  optionValue: 'id' as const,
  onSearch: (value: string) => [
    {
      field: 'filter[name]',
      operator: 'eq' as const,
      value
    }
  ],
  pagination: {
    mode: 'server' as const,
    pageSize: 100
  }
};

function useSelectCourseByType(
  defaultType?: 'Course' | 'Graduate::Discipline' | 'ExternalItem',
  graduateResource?: 'graduate_courses' | 'graduate_disciplines',
  defaultValue?: string
) {
  const [selectedCourseType, setSelectedCourseType] = useState(
    defaultType || 'Course'
  );

  useEffect(() => {
    if (defaultType) {
      setSelectedCourseType(defaultType);
    }
  }, [defaultType]);

  const tenant = getTenant();
  const externalItemsEnabled =
    !tenant.disabledFeatures?.includes('external_items');

  const isCourse = selectedCourseType === 'Course';
  const isExternalItem =
    selectedCourseType === 'ExternalItem' && externalItemsEnabled;
  const isGraduate = !isCourse && !isExternalItem;

  const { selectProps: courseSelectProps } = useSelect<ICourse>({
    resource: 'courses',
    ...selectSearchConfig,
    defaultValue: isCourse ? defaultValue : undefined,
    queryOptions: {
      enabled: isCourse
    }
  });

  const { selectProps: graduateSelectProps } = useSelect<
    IGraduateCourse | IGraduateDiscipline
  >({
    resource: graduateResource ?? 'graduate_courses',
    ...selectSearchConfig,
    defaultValue: isGraduate ? defaultValue : undefined,
    queryOptions: {
      enabled: isGraduate
    }
  });

  const { selectProps: externalSelectProps } = useSelect<IExternalItem>({
    resource: 'external_items',
    ...selectSearchConfig,
    defaultValue: isExternalItem ? defaultValue : undefined,
    queryOptions: {
      enabled: isExternalItem
    }
  });

  const selectProps = useMemo(() => {
    if (isCourse) {
      return courseSelectProps;
    }

    if (isExternalItem) {
      return externalSelectProps;
    }

    return graduateSelectProps;
  }, [
    courseSelectProps,
    externalSelectProps,
    graduateSelectProps,
    isCourse,
    isExternalItem
  ]);

  return {
    selectedCourseType,
    setSelectedCourseType,
    selectProps
  };
}

export default useSelectCourseByType;
